import { StateCreator } from 'zustand';
import {
  Client,
  Specification,
  SpecificationConfig,
  SpecificationStatistics,
  SpecificationTemplate,
  SpecificationSection,
  FunctionalRequirement,
  TechnicalRequirement,
  NonFunctionalRequirement,
  Deliverable,
  ProjectConstraint,
  DEFAULT_SPECIFICATION_CONFIG,
} from '../../types/specification';
import {
  mapClient, mapDocument,
  apiListClients, apiCreateClient, apiUpdateClient, apiDeleteClient,
  apiListDocuments, apiCreateDocument, apiUpdateDocument, apiDeleteDocument,
  apiDuplicateDocument, apiUpdateDocumentStatus,
  apiCreateSection, apiUpdateSection, apiDeleteSection, apiReorderSections,
  apiCreateRequirement, apiUpdateRequirement, apiDeleteRequirement,
  apiCreateDeliverable, apiUpdateDeliverable, apiDeleteDeliverable,
  apiCreateConstraint, apiUpdateConstraint, apiDeleteConstraint,
} from '../../api/specifications';

export interface SpecificationSlice {
  // State
  clients: Client[];
  specifications: Specification[];
  specificationConfig: SpecificationConfig;
  specLoading: boolean;
  selectedClientId: string | null;
  selectedSpecificationId: string | null;

  // Client Actions
  addClient: (client: Omit<Client, 'id' | 'createdAt' | 'updatedAt'>) => string;
  updateClient: (clientId: string, updates: Partial<Client>) => void;
  deleteClient: (clientId: string) => void;
  getClientById: (clientId: string) => Client | undefined;
  setSelectedClient: (clientId: string | null) => void;

  // Specification Actions
  addSpecification: (spec: Omit<Specification, 'id' | 'createdAt' | 'updatedAt'>) => string;
  updateSpecification: (specId: string, updates: Partial<Specification>) => void;
  deleteSpecification: (specId: string) => void;
  duplicateSpecification: (specId: string) => string | null;
  getSpecificationById: (specId: string) => Specification | undefined;
  getSpecificationsByClient: (clientId: string) => Specification[];
  getSpecificationsByProject: (projectId: string) => Specification[];
  setSelectedSpecification: (specId: string | null) => void;

  // Section Management
  addSection: (specId: string, section: Omit<SpecificationSection, 'id'>) => void;
  updateSection: (specId: string, sectionId: string, updates: Partial<SpecificationSection>) => void;
  deleteSection: (specId: string, sectionId: string) => void;
  reorderSections: (specId: string, sectionIds: string[]) => void;

  // Functional Requirements
  addFunctionalRequirement: (specId: string, req: Omit<FunctionalRequirement, 'id'>) => void;
  updateFunctionalRequirement: (specId: string, reqId: string, updates: Partial<FunctionalRequirement>) => void;
  deleteFunctionalRequirement: (specId: string, reqId: string) => void;

  // Technical Requirements
  addTechnicalRequirement: (specId: string, req: Omit<TechnicalRequirement, 'id'>) => void;
  updateTechnicalRequirement: (specId: string, reqId: string, updates: Partial<TechnicalRequirement>) => void;
  deleteTechnicalRequirement: (specId: string, reqId: string) => void;

  // Non-Functional Requirements
  addNonFunctionalRequirement: (specId: string, req: Omit<NonFunctionalRequirement, 'id'>) => void;
  updateNonFunctionalRequirement: (specId: string, reqId: string, updates: Partial<NonFunctionalRequirement>) => void;
  deleteNonFunctionalRequirement: (specId: string, reqId: string) => void;

  // Deliverables
  addDeliverable: (specId: string, deliverable: Omit<Deliverable, 'id'>) => void;
  updateDeliverable: (specId: string, deliverableId: string, updates: Partial<Deliverable>) => void;
  deleteDeliverable: (specId: string, deliverableId: string) => void;

  // Constraints
  addConstraint: (specId: string, constraint: Omit<ProjectConstraint, 'id'>) => void;
  updateConstraint: (specId: string, constraintId: string, updates: Partial<ProjectConstraint>) => void;
  deleteConstraint: (specId: string, constraintId: string) => void;

  // Templates
  addTemplate: (template: Omit<SpecificationTemplate, 'id'>) => void;
  deleteTemplate: (templateId: string) => void;
  applyTemplate: (specId: string, templateId: string) => void;

  // Status Management
  updateSpecificationStatus: (specId: string, status: Specification['status']) => void;
  approveSpecification: (specId: string, approvedBy: string) => void;
  signSpecification: (specId: string, signedBy: string) => void;

  // Backend sync
  loadSpecData: () => Promise<void>;

  // Utilities
  getSpecificationStatistics: () => SpecificationStatistics;
  updateSpecificationConfig: (updates: Partial<SpecificationConfig>) => void;
  searchSpecifications: (query: string) => Specification[];
  getNextSpecificationReference: () => string;
}

const generateId = (): string => {
  return `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
};

export const createSpecificationSlice: StateCreator<any, [], [], SpecificationSlice> = (set, get) => {
  const canEdit = (): boolean => {
    const state = get();
    const role = state.currentProject?.currentUserRole;
    if (role) {
      const allowed = role === 'OWNER' || role === 'ADMIN' || role === 'EDITOR';
      if (!allowed) {
        state.addNotification?.('warning', 'Droits insuffisants pour modifier ce contenu.');
      }
      return allowed;
    }
    if (state.storageMode === 'backend') {
      state.addNotification?.('warning', 'Droits insuffisants pour modifier ce contenu.');
      return false;
    }
    return true;
  };

  return ({
  // Initial State
  clients: [],
  specifications: [],
  specificationConfig: DEFAULT_SPECIFICATION_CONFIG,
  selectedClientId: null,
  selectedSpecificationId: null,
  specLoading: false,

  // ── Backend sync ───────────────────────────────────────────────────────────
  loadSpecData: async () => {
    if (get().storageMode !== 'backend') return;
    set({ specLoading: true });
    try {
      const [{ clients: bClients }, { documents: bDocs }] = await Promise.all([
        apiListClients(),
        apiListDocuments(),
      ]);
      set({
        clients: bClients.map(mapClient),
        specifications: bDocs.map(mapDocument),
        specLoading: false,
      });
    } catch {
      set({ specLoading: false });
    }
  },

  // Client Actions
  addClient: (clientData) => {
    if (!canEdit()) return '';
    const id = generateId();
    const newClient: Client = {
      ...clientData,
      id,
      createdAt: new Date(),
      updatedAt: new Date(),
    };

    set((state: any) => ({
      clients: [...state.clients, newClient],
    }));

    if (get().storageMode === 'backend') {
      apiCreateClient(clientData)
        .then(({ client: bc }) => {
          set((state: any) => ({
            clients: state.clients.map((c: Client) =>
              c.id === id ? mapClient(bc) : c
            ),
          }));
        })
        .catch(() => {
          set((state: any) => ({
            clients: state.clients.filter((c: Client) => c.id !== id),
          }));
          get().addNotification?.('error', 'Erreur lors de la création du client');
        });
    }

    return id;
  },

  updateClient: (clientId, updates) => {
    if (!canEdit()) return;
    set((state: any) => ({
      clients: state.clients.map((client: Client) =>
        client.id === clientId
          ? { ...client, ...updates, updatedAt: new Date() }
          : client
      ),
    }));
    if (get().storageMode === 'backend') {
      apiUpdateClient(clientId, updates).then(({ client: bc }) => {
        set((state: any) => ({
          clients: state.clients.map((c: Client) =>
            c.id === clientId ? mapClient(bc) : c
          ),
        }));
      }).catch(() => {
        get().addNotification?.('error', 'Erreur lors de la mise à jour du client');
      });
    }
  },

  deleteClient: (clientId) => {
    if (!canEdit()) return;
    const prev = get().clients;
    const prevSpecs = get().specifications;
    set((state: any) => ({
      clients: state.clients.filter((client: Client) => client.id !== clientId),
      specifications: state.specifications.filter((spec: Specification) => spec.clientId !== clientId),
      selectedClientId: state.selectedClientId === clientId ? null : state.selectedClientId,
    }));
    if (get().storageMode === 'backend') {
      apiDeleteClient(clientId).catch(() => {
        set({ clients: prev, specifications: prevSpecs });
        get().addNotification?.('error', 'Erreur lors de la suppression du client');
      });
    }
  },

  getClientById: (clientId) => {
    const state = get();
    return state.clients.find((client: Client) => client.id === clientId);
  },

  setSelectedClient: (clientId) => {
    set({ selectedClientId: clientId });
  },

  // Specification Actions
  addSpecification: (specData) => {
    if (!canEdit()) return '';
    const id = generateId();
    const newSpec: Specification = {
      ...specData,
      id,
      createdAt: new Date(),
      updatedAt: new Date(),
    };

    set((state: any) => ({
      specifications: [...state.specifications, newSpec],
    }));

    if (get().storageMode === 'backend') {
      apiCreateDocument({
        clientId: specData.clientId,
        title: specData.title,
        projectId: specData.projectId,
        version: specData.version,
        projectContext: specData.projectContext,
        projectObjectives: specData.projectObjectives,
        projectScope: specData.projectScope,
        estimatedStartDate: specData.estimatedStartDate?.toISOString(),
        estimatedEndDate: specData.estimatedEndDate?.toISOString(),
        estimatedBudget: specData.estimatedBudget,
        currency: specData.currency,
        stakeholders: specData.stakeholders,
        notes: specData.notes,
      }).then(({ document: bd }) => {
        set((state: any) => ({
          specifications: state.specifications.map((s: Specification) =>
            s.id === id ? mapDocument(bd) : s
          ),
        }));
      }).catch(() => {
        set((state: any) => ({
          specifications: state.specifications.filter((s: Specification) => s.id !== id),
        }));
        get().addNotification?.('error', 'Erreur lors de la création du cahier des charges');
      });
    }

    return id;
  },

  updateSpecification: (specId, updates) => {
    if (!canEdit()) return;
    set((state: any) => ({
      specifications: state.specifications.map((spec: Specification) =>
        spec.id === specId
          ? { ...spec, ...updates, updatedAt: new Date() }
          : spec
      ),
    }));
    if (get().storageMode === 'backend') {
      const payload: Record<string, unknown> = { ...updates };
      if (updates.estimatedStartDate instanceof Date)
        payload.estimatedStartDate = updates.estimatedStartDate.toISOString();
      if (updates.estimatedEndDate instanceof Date)
        payload.estimatedEndDate = updates.estimatedEndDate.toISOString();
      apiUpdateDocument(specId, payload).then(({ document: bd }) => {
        set((state: any) => ({
          specifications: state.specifications.map((s: Specification) =>
            s.id === specId ? mapDocument(bd) : s
          ),
        }));
      }).catch(() => {
        get().addNotification?.('error', 'Erreur lors de la mise à jour du cahier des charges');
      });
    }
  },

  deleteSpecification: (specId) => {
    if (!canEdit()) return;
    const prev = get().specifications;
    set((state: any) => ({
      specifications: state.specifications.filter((spec: Specification) => spec.id !== specId),
      selectedSpecificationId: state.selectedSpecificationId === specId ? null : state.selectedSpecificationId,
    }));
    if (get().storageMode === 'backend') {
      apiDeleteDocument(specId).catch(() => {
        set({ specifications: prev });
        get().addNotification?.('error', 'Erreur lors de la suppression du cahier des charges');
      });
    }
  },

  duplicateSpecification: (specId) => {
    if (!canEdit()) return null;
    const state = get();
    const original = state.specifications.find((spec: Specification) => spec.id === specId);

    if (!original) return null;

    const id = generateId();
    const duplicate: Specification = {
      ...original,
      id,
      reference: state.getNextSpecificationReference(),
      title: `${original.title} (Copie)`,
      status: 'draft',
      createdAt: new Date(),
      updatedAt: new Date(),
      approvedBy: undefined,
      approvedAt: undefined,
      signedBy: undefined,
      signedAt: undefined,
    };

    set((state: any) => ({
      specifications: [...state.specifications, duplicate],
    }));

    if (get().storageMode === 'backend') {
      apiDuplicateDocument(specId).then(({ document: bd }) => {
        set((state: any) => ({
          specifications: state.specifications.map((s: Specification) =>
            s.id === id ? mapDocument(bd) : s
          ),
        }));
      }).catch(() => {
        set((state: any) => ({
          specifications: state.specifications.filter((s: Specification) => s.id !== id),
        }));
        get().addNotification?.('error', 'Erreur lors de la duplication');
      });
    }

    return id;
  },

  getSpecificationById: (specId) => {
    const state = get();
    return state.specifications.find((spec: Specification) => spec.id === specId);
  },

  getSpecificationsByClient: (clientId) => {
    const state = get();
    return state.specifications.filter((spec: Specification) => spec.clientId === clientId);
  },

  getSpecificationsByProject: (projectId) => {
    const state = get();
    return state.specifications.filter((spec: Specification) => spec.projectId === projectId);
  },

  setSelectedSpecification: (specId) => {
    set({ selectedSpecificationId: specId });
  },

  // Section Management
  addSection: (specId, sectionData) => {
    if (!canEdit()) return;
    const sectionId = generateId();
    const newSection: SpecificationSection = {
      ...sectionData,
      id: sectionId,
    };

    set((state: any) => ({
      specifications: state.specifications.map((spec: Specification) =>
        spec.id === specId
          ? {
              ...spec,
              sections: [...spec.sections, newSection],
              updatedAt: new Date(),
            }
          : spec
      ),
    }));

    if (get().storageMode === 'backend') {
      apiCreateSection(specId, sectionData).then(({ section: bs }) => {
        set((state: any) => ({
          specifications: state.specifications.map((spec: Specification) =>
            spec.id === specId
              ? {
                  ...spec,
                  sections: spec.sections.map((s) =>
                    s.id === sectionId ? { ...s, id: bs.id } : s
                  ),
                }
              : spec
          ),
        }));
      }).catch(() => {
        set((state: any) => ({
          specifications: state.specifications.map((spec: Specification) =>
            spec.id === specId
              ? { ...spec, sections: spec.sections.filter((s) => s.id !== sectionId) }
              : spec
          ),
        }));
      });
    }
  },

  updateSection: (specId, sectionId, updates) => {
    if (!canEdit()) return;
    set((state: any) => ({
      specifications: state.specifications.map((spec: Specification) =>
        spec.id === specId
          ? {
              ...spec,
              sections: spec.sections.map((section) =>
                section.id === sectionId ? { ...section, ...updates } : section
              ),
              updatedAt: new Date(),
            }
          : spec
      ),
    }));
    if (get().storageMode === 'backend') {
      apiUpdateSection(specId, sectionId, updates).catch(() => {
        get().addNotification?.('error', 'Erreur lors de la mise à jour de la section');
      });
    }
  },

  deleteSection: (specId, sectionId) => {
    if (!canEdit()) return;
    const prevSpecs = get().specifications;
    set((state: any) => ({
      specifications: state.specifications.map((spec: Specification) =>
        spec.id === specId
          ? {
              ...spec,
              sections: spec.sections.filter((section) => section.id !== sectionId),
              updatedAt: new Date(),
            }
          : spec
      ),
    }));
    if (get().storageMode === 'backend') {
      apiDeleteSection(specId, sectionId).catch(() => {
        set({ specifications: prevSpecs });
        get().addNotification?.('error', 'Erreur lors de la suppression de la section');
      });
    }
  },

  reorderSections: (specId, sectionIds) => {
    set((state: any) => ({
      specifications: state.specifications.map((spec: Specification) => {
        if (spec.id !== specId) return spec;

        const sectionsMap = new Map(spec.sections.map((s) => [s.id, s]));
        const reorderedSections = sectionIds
          .map((id, index) => {
            const section = sectionsMap.get(id);
            return section ? { ...section, order: index } : null;
          })
          .filter((s): s is SpecificationSection => s !== null);

        return {
          ...spec,
          sections: reorderedSections,
          updatedAt: new Date(),
        };
      }),
    }));
    if (get().storageMode === 'backend') {
      apiReorderSections(specId, sectionIds).catch(() => {
        get().addNotification?.('error', 'Erreur lors du réordonnancement des sections');
      });
    }
  },

  // Functional Requirements
  addFunctionalRequirement: (specId, reqData) => {
    if (!canEdit()) return;
    const reqId = generateId();
    const newReq: FunctionalRequirement = { ...reqData, id: reqId };
    set((state: any) => ({
      specifications: state.specifications.map((spec: Specification) =>
        spec.id === specId
          ? { ...spec, functionalRequirements: [...spec.functionalRequirements, newReq], updatedAt: new Date() }
          : spec
      ),
    }));
    if (get().storageMode === 'backend') {
      apiCreateRequirement(specId, { ...reqData, type: 'functional' }).then(({ requirement: br }) => {
        set((state: any) => ({
          specifications: state.specifications.map((spec: Specification) =>
            spec.id === specId
              ? { ...spec, functionalRequirements: spec.functionalRequirements.map((r) => r.id === reqId ? { ...r, id: br.id } : r) }
              : spec
          ),
        }));
      }).catch(() => {
        set((state: any) => ({
          specifications: state.specifications.map((spec: Specification) =>
            spec.id === specId
              ? { ...spec, functionalRequirements: spec.functionalRequirements.filter((r) => r.id !== reqId) }
              : spec
          ),
        }));
      });
    }
  },

  updateFunctionalRequirement: (specId, reqId, updates) => {
    if (!canEdit()) return;
    set((state: any) => ({
      specifications: state.specifications.map((spec: Specification) =>
        spec.id === specId
          ? { ...spec, functionalRequirements: spec.functionalRequirements.map((req) => req.id === reqId ? { ...req, ...updates } : req), updatedAt: new Date() }
          : spec
      ),
    }));
    if (get().storageMode === 'backend') {
      apiUpdateRequirement(specId, reqId, updates as Record<string, unknown>).catch(() => {
        get().addNotification?.('error', 'Erreur lors de la mise à jour de l\'exigence');
      });
    }
  },

  deleteFunctionalRequirement: (specId, reqId) => {
    if (!canEdit()) return;
    const prevSpecs = get().specifications;
    set((state: any) => ({
      specifications: state.specifications.map((spec: Specification) =>
        spec.id === specId
          ? { ...spec, functionalRequirements: spec.functionalRequirements.filter((req) => req.id !== reqId), updatedAt: new Date() }
          : spec
      ),
    }));
    if (get().storageMode === 'backend') {
      apiDeleteRequirement(specId, reqId).catch(() => {
        set({ specifications: prevSpecs });
      });
    }
  },

  // Technical Requirements
  addTechnicalRequirement: (specId, reqData) => {
    if (!canEdit()) return;
    const reqId = generateId();
    const newReq: TechnicalRequirement = { ...reqData, id: reqId };
    set((state: any) => ({
      specifications: state.specifications.map((spec: Specification) =>
        spec.id === specId
          ? { ...spec, technicalRequirements: [...spec.technicalRequirements, newReq], updatedAt: new Date() }
          : spec
      ),
    }));
    if (get().storageMode === 'backend') {
      apiCreateRequirement(specId, { ...reqData, type: 'technical' }).then(({ requirement: br }) => {
        set((state: any) => ({
          specifications: state.specifications.map((spec: Specification) =>
            spec.id === specId
              ? { ...spec, technicalRequirements: spec.technicalRequirements.map((r) => r.id === reqId ? { ...r, id: br.id } : r) }
              : spec
          ),
        }));
      }).catch(() => {
        set((state: any) => ({
          specifications: state.specifications.map((spec: Specification) =>
            spec.id === specId
              ? { ...spec, technicalRequirements: spec.technicalRequirements.filter((r) => r.id !== reqId) }
              : spec
          ),
        }));
      });
    }
  },

  updateTechnicalRequirement: (specId, reqId, updates) => {
    if (!canEdit()) return;
    set((state: any) => ({
      specifications: state.specifications.map((spec: Specification) =>
        spec.id === specId
          ? { ...spec, technicalRequirements: spec.technicalRequirements.map((req) => req.id === reqId ? { ...req, ...updates } : req), updatedAt: new Date() }
          : spec
      ),
    }));
    if (get().storageMode === 'backend') {
      apiUpdateRequirement(specId, reqId, updates as Record<string, unknown>).catch(() => {
        get().addNotification?.('error', 'Erreur lors de la mise à jour de l\'exigence technique');
      });
    }
  },

  deleteTechnicalRequirement: (specId, reqId) => {
    if (!canEdit()) return;
    const prevSpecs = get().specifications;
    set((state: any) => ({
      specifications: state.specifications.map((spec: Specification) =>
        spec.id === specId
          ? { ...spec, technicalRequirements: spec.technicalRequirements.filter((req) => req.id !== reqId), updatedAt: new Date() }
          : spec
      ),
    }));
    if (get().storageMode === 'backend') {
      apiDeleteRequirement(specId, reqId).catch(() => {
        set({ specifications: prevSpecs });
      });
    }
  },

  // Non-Functional Requirements
  addNonFunctionalRequirement: (specId, reqData) => {
    if (!canEdit()) return;
    const reqId = generateId();
    const newReq: NonFunctionalRequirement = { ...reqData, id: reqId };
    set((state: any) => ({
      specifications: state.specifications.map((spec: Specification) =>
        spec.id === specId
          ? { ...spec, nonFunctionalRequirements: [...spec.nonFunctionalRequirements, newReq], updatedAt: new Date() }
          : spec
      ),
    }));
    if (get().storageMode === 'backend') {
      apiCreateRequirement(specId, { ...reqData, type: 'non_functional' } as Record<string, unknown>).then(({ requirement: br }) => {
        set((state: any) => ({
          specifications: state.specifications.map((spec: Specification) =>
            spec.id === specId
              ? { ...spec, nonFunctionalRequirements: spec.nonFunctionalRequirements.map((r) => r.id === reqId ? { ...r, id: br.id } : r) }
              : spec
          ),
        }));
      }).catch(() => {
        set((state: any) => ({
          specifications: state.specifications.map((spec: Specification) =>
            spec.id === specId
              ? { ...spec, nonFunctionalRequirements: spec.nonFunctionalRequirements.filter((r) => r.id !== reqId) }
              : spec
          ),
        }));
      });
    }
  },

  updateNonFunctionalRequirement: (specId, reqId, updates) => {
    if (!canEdit()) return;
    set((state: any) => ({
      specifications: state.specifications.map((spec: Specification) =>
        spec.id === specId
          ? { ...spec, nonFunctionalRequirements: spec.nonFunctionalRequirements.map((req) => req.id === reqId ? { ...req, ...updates } : req), updatedAt: new Date() }
          : spec
      ),
    }));
    if (get().storageMode === 'backend') {
      apiUpdateRequirement(specId, reqId, updates as Record<string, unknown>).catch(() => {
        get().addNotification?.('error', 'Erreur lors de la mise à jour de l\'exigence non fonctionnelle');
      });
    }
  },

  deleteNonFunctionalRequirement: (specId, reqId) => {
    if (!canEdit()) return;
    const prevSpecs = get().specifications;
    set((state: any) => ({
      specifications: state.specifications.map((spec: Specification) =>
        spec.id === specId
          ? { ...spec, nonFunctionalRequirements: spec.nonFunctionalRequirements.filter((req) => req.id !== reqId), updatedAt: new Date() }
          : spec
      ),
    }));
    if (get().storageMode === 'backend') {
      apiDeleteRequirement(specId, reqId).catch(() => {
        set({ specifications: prevSpecs });
      });
    }
  },

  // Deliverables
  addDeliverable: (specId, deliverableData) => {
    if (!canEdit()) return;
    const deliverableId = generateId();
    const newDeliverable: Deliverable = { ...deliverableData, id: deliverableId };
    set((state: any) => ({
      specifications: state.specifications.map((spec: Specification) =>
        spec.id === specId
          ? { ...spec, deliverables: [...spec.deliverables, newDeliverable], updatedAt: new Date() }
          : spec
      ),
    }));
    if (get().storageMode === 'backend') {
      const payload: Record<string, unknown> = { ...deliverableData };
      if (deliverableData.dueDate instanceof Date) payload.dueDate = deliverableData.dueDate.toISOString();
      apiCreateDeliverable(specId, payload).then(({ deliverable: bd }) => {
        set((state: any) => ({
          specifications: state.specifications.map((spec: Specification) =>
            spec.id === specId
              ? { ...spec, deliverables: spec.deliverables.map((d) => d.id === deliverableId ? { ...d, id: bd.id } : d) }
              : spec
          ),
        }));
      }).catch(() => {
        set((state: any) => ({
          specifications: state.specifications.map((spec: Specification) =>
            spec.id === specId
              ? { ...spec, deliverables: spec.deliverables.filter((d) => d.id !== deliverableId) }
              : spec
          ),
        }));
      });
    }
  },

  updateDeliverable: (specId, deliverableId, updates) => {
    if (!canEdit()) return;
    set((state: any) => ({
      specifications: state.specifications.map((spec: Specification) =>
        spec.id === specId
          ? { ...spec, deliverables: spec.deliverables.map((d) => d.id === deliverableId ? { ...d, ...updates } : d), updatedAt: new Date() }
          : spec
      ),
    }));
    if (get().storageMode === 'backend') {
      const payload: Record<string, unknown> = { ...updates };
      if (updates.dueDate instanceof Date) payload.dueDate = updates.dueDate.toISOString();
      apiUpdateDeliverable(specId, deliverableId, payload).catch(() => {
        get().addNotification?.('error', 'Erreur lors de la mise à jour du livrable');
      });
    }
  },

  deleteDeliverable: (specId, deliverableId) => {
    if (!canEdit()) return;
    const prevSpecs = get().specifications;
    set((state: any) => ({
      specifications: state.specifications.map((spec: Specification) =>
        spec.id === specId
          ? { ...spec, deliverables: spec.deliverables.filter((d) => d.id !== deliverableId), updatedAt: new Date() }
          : spec
      ),
    }));
    if (get().storageMode === 'backend') {
      apiDeleteDeliverable(specId, deliverableId).catch(() => {
        set({ specifications: prevSpecs });
      });
    }
  },

  // Constraints
  addConstraint: (specId, constraintData) => {
    if (!canEdit()) return;
    const constraintId = generateId();
    const newConstraint: ProjectConstraint = { ...constraintData, id: constraintId };
    set((state: any) => ({
      specifications: state.specifications.map((spec: Specification) =>
        spec.id === specId
          ? { ...spec, constraints: [...spec.constraints, newConstraint], updatedAt: new Date() }
          : spec
      ),
    }));
    if (get().storageMode === 'backend') {
      apiCreateConstraint(specId, constraintData as Record<string, unknown>).then(({ constraint: bc }) => {
        set((state: any) => ({
          specifications: state.specifications.map((spec: Specification) =>
            spec.id === specId
              ? { ...spec, constraints: spec.constraints.map((c) => c.id === constraintId ? { ...c, id: bc.id } : c) }
              : spec
          ),
        }));
      }).catch(() => {
        set((state: any) => ({
          specifications: state.specifications.map((spec: Specification) =>
            spec.id === specId
              ? { ...spec, constraints: spec.constraints.filter((c) => c.id !== constraintId) }
              : spec
          ),
        }));
      });
    }
  },

  updateConstraint: (specId, constraintId, updates) => {
    if (!canEdit()) return;
    set((state: any) => ({
      specifications: state.specifications.map((spec: Specification) =>
        spec.id === specId
          ? { ...spec, constraints: spec.constraints.map((c) => c.id === constraintId ? { ...c, ...updates } : c), updatedAt: new Date() }
          : spec
      ),
    }));
    if (get().storageMode === 'backend') {
      apiUpdateConstraint(specId, constraintId, updates as Record<string, unknown>).catch(() => {
        get().addNotification?.('error', 'Erreur lors de la mise à jour de la contrainte');
      });
    }
  },

  deleteConstraint: (specId, constraintId) => {
    if (!canEdit()) return;
    const prevSpecs = get().specifications;
    set((state: any) => ({
      specifications: state.specifications.map((spec: Specification) =>
        spec.id === specId
          ? { ...spec, constraints: spec.constraints.filter((c) => c.id !== constraintId), updatedAt: new Date() }
          : spec
      ),
    }));
    if (get().storageMode === 'backend') {
      apiDeleteConstraint(specId, constraintId).catch(() => {
        set({ specifications: prevSpecs });
      });
    }
  },

  // Templates
  addTemplate: (templateData) => {
    const templateId = generateId();
    const newTemplate: SpecificationTemplate = {
      ...templateData,
      id: templateId,
    };

    set((state: any) => ({
      specificationConfig: {
        ...state.specificationConfig,
        templates: [...state.specificationConfig.templates, newTemplate],
      },
    }));
  },

  deleteTemplate: (templateId) => {
    set((state: any) => ({
      specificationConfig: {
        ...state.specificationConfig,
        templates: state.specificationConfig.templates.filter(
          (t: SpecificationTemplate) => t.id !== templateId
        ),
      },
    }));
  },

  applyTemplate: (specId, templateId) => {
    const state = get();
    const template = state.specificationConfig.templates.find(
      (t: SpecificationTemplate) => t.id === templateId
    );

    if (!template) return;

    set((state: any) => ({
      specifications: state.specifications.map((spec: Specification) => {
        if (spec.id !== specId) return spec;

        const newSections = template.sections.map((s: Omit<SpecificationSection, 'id'>, index: number) => ({
          ...s,
          id: generateId(),
          order: spec.sections.length + index,
        }));

        const newFuncReqs = template.defaultFunctionalRequirements?.map((r: Omit<FunctionalRequirement, 'id'>) => ({
          ...r,
          id: generateId(),
        })) || [];

        const newTechReqs = template.defaultTechnicalRequirements?.map((r: Omit<TechnicalRequirement, 'id'>) => ({
          ...r,
          id: generateId(),
        })) || [];

        const newNfReqs = template.defaultNonFunctionalRequirements?.map((r: Omit<NonFunctionalRequirement, 'id'>) => ({
          ...r,
          id: generateId(),
        })) || [];

        return {
          ...spec,
          sections: [...spec.sections, ...newSections],
          functionalRequirements: [...spec.functionalRequirements, ...newFuncReqs],
          technicalRequirements: [...spec.technicalRequirements, ...newTechReqs],
          nonFunctionalRequirements: [...spec.nonFunctionalRequirements, ...newNfReqs],
          updatedAt: new Date(),
        };
      }),
    }));
  },

  // Status Management
  updateSpecificationStatus: (specId, status) => {
    if (!canEdit()) return;
    set((state: any) => ({
      specifications: state.specifications.map((spec: Specification) =>
        spec.id === specId ? { ...spec, status, updatedAt: new Date() } : spec
      ),
    }));
    if (get().storageMode === 'backend') {
      const backendStatus = status.toUpperCase().replace('_', '_') as string;
      apiUpdateDocumentStatus(specId, backendStatus).then(({ document: bd }) => {
        set((state: any) => ({
          specifications: state.specifications.map((s: Specification) =>
            s.id === specId ? mapDocument(bd) : s
          ),
        }));
      }).catch(() => {
        get().addNotification?.('error', 'Erreur lors du changement de statut');
      });
    }
  },

  approveSpecification: (specId, approvedBy) => {
    if (!canEdit()) return;
    set((state: any) => ({
      specifications: state.specifications.map((spec: Specification) =>
        spec.id === specId
          ? { ...spec, status: 'approved' as const, approvedBy, approvedAt: new Date(), updatedAt: new Date() }
          : spec
      ),
    }));
    if (get().storageMode === 'backend') {
      apiUpdateDocumentStatus(specId, 'APPROVED', approvedBy).then(({ document: bd }) => {
        set((state: any) => ({
          specifications: state.specifications.map((s: Specification) =>
            s.id === specId ? mapDocument(bd) : s
          ),
        }));
      }).catch(() => {
        get().addNotification?.('error', 'Erreur lors de l\'approbation');
      });
    }
  },

  signSpecification: (specId, signedBy) => {
    if (!canEdit()) return;
    set((state: any) => ({
      specifications: state.specifications.map((spec: Specification) =>
        spec.id === specId
          ? { ...spec, status: 'signed' as const, signedBy, signedAt: new Date(), updatedAt: new Date() }
          : spec
      ),
    }));
    if (get().storageMode === 'backend') {
      apiUpdateDocumentStatus(specId, 'SIGNED', signedBy).then(({ document: bd }) => {
        set((state: any) => ({
          specifications: state.specifications.map((s: Specification) =>
            s.id === specId ? mapDocument(bd) : s
          ),
        }));
      }).catch(() => {
        get().addNotification?.('error', 'Erreur lors de la signature');
      });
    }
  },

  // Utilities
  getSpecificationStatistics: () => {
    const state = get();
    const specs = state.specifications as Specification[];

    const statusCounts = specs.reduce((acc, spec) => {
      acc[spec.status] = (acc[spec.status] || 0) + 1;
      return acc;
    }, {} as Record<string, number>);

    const totalFunctionalReqs = specs.reduce(
      (sum, spec) => sum + spec.functionalRequirements.length,
      0
    );
    const totalTechnicalReqs = specs.reduce(
      (sum, spec) => sum + spec.technicalRequirements.length,
      0
    );
    const totalNonFunctionalReqs = specs.reduce(
      (sum, spec) => sum + spec.nonFunctionalRequirements.length,
      0
    );

    const recentSpecs = [...specs]
      .sort((a, b) => b.updatedAt.getTime() - a.updatedAt.getTime())
      .slice(0, 5);

    return {
      totalClients: state.clients.length,
      totalSpecifications: specs.length,
      specificationsByStatus: statusCounts as any,
      averageRequirementsPerSpec:
        specs.length > 0
          ? (totalFunctionalReqs + totalTechnicalReqs) / specs.length
          : 0,
      totalFunctionalRequirements: totalFunctionalReqs,
      totalTechnicalRequirements: totalTechnicalReqs,
      totalNonFunctionalRequirements: totalNonFunctionalReqs,
      recentSpecifications: recentSpecs,
    };
  },

  updateSpecificationConfig: (updates) => {
    set((state: any) => ({
      specificationConfig: {
        ...state.specificationConfig,
        ...updates,
      },
    }));
  },

  searchSpecifications: (query) => {
    const state = get();
    const lowercaseQuery = query.toLowerCase();

    return state.specifications.filter((spec: Specification) => {
      return (
        spec.title.toLowerCase().includes(lowercaseQuery) ||
        spec.reference.toLowerCase().includes(lowercaseQuery) ||
        spec.projectContext.toLowerCase().includes(lowercaseQuery) ||
        spec.projectObjectives.toLowerCase().includes(lowercaseQuery)
      );
    });
  },

  getNextSpecificationReference: () => {
    const state = get();
    const year = new Date().getFullYear();
    const specsThisYear = state.specifications.filter((spec: Specification) =>
      spec.reference.startsWith(`CDC-${year}`)
    );
    const nextNumber = specsThisYear.length + 1;
    return `CDC-${year}-${String(nextNumber).padStart(3, '0')}`;
  },
  });
};
